import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, PackageCheck, Radar, Handshake, Headset, ClipboardCheck, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1651928977880-ffb2d963b6b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";

function useInView(ref) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setSeen(true),
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return seen;
}

function CountUp({ target, decimals = 0, suffix = "", started }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    const dur = 1800;
    const t0 = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, target]);
  const display = decimals
    ? val.toFixed(decimals)
    : Math.round(val).toLocaleString("en-IN");
  return <span>{display}{suffix}</span>;
}

const stats = [
  { target: 5000000, suffix: "+", label: "Km Route Coverage", text: null },
  { text: "Trusted", label: "Business Partners" },
  { text: "Wide Network", label: "Associate Vehicles" },
  { text: "On-Time", label: "Commitment to Timelines" },
];

const services = [
  { icon: Truck, title: "Full Truckload (FTL)", desc: "Dedicated trucks for your complete consignment — direct routes, faster transit, and zero transhipment risk." },
  { icon: PackageCheck, title: "Safe Consignment Handling", desc: "Trained loading teams, secure lashing, and careful documentation for every bilty from pickup to delivery." },
  { icon: Radar, title: "24/7 Real-Time Tracking", desc: "Round-the-clock vehicle monitoring and proactive status updates so you always know where your goods are." },
];

const trust = [
  { icon: Handshake, title: "Reliable Vendor Network", desc: "We collaborate with a thoroughly vetted network of associate transport partners to ensure safe transit." },
  { icon: Headset, title: "Seamless Coordination & Support", desc: "Dedicated end-to-end coordination providing you with proactive transit updates from loading to delivery." },
  { icon: ClipboardCheck, title: "Careful Freight Supervision", desc: "Strict oversight during loading and expert route planning to maximize the safety of your valuable cargo." },
];

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function HomePage() {
  const statsRef = useRef(null);
  const statsSeen = useInView(statsRef);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[82vh] flex items-center px-6 lg:px-16 overflow-hidden">
        <img src={HERO_IMG} alt="Highway truck transport" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/75 to-slate-900/40" />
        <motion.div
          className="relative z-10 max-w-3xl py-24"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-400 mb-5">
            Trusted Transport Partner Since Day One
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
            Reliable Logistics &amp; Safe Transport <span className="text-orange-500">Across India</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
            From single bilty to full fleet movements — JAI JAGDAMBA LOGISTICS delivers your consignments on time, every time, with complete safety.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/gallery" data-testid="hero-explore-fleet-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-8 py-4 rounded-md shadow-lg shadow-orange-500/30 hover:bg-orange-600 transition-colors duration-200">
              Our Services <ArrowRight size={18} />
            </Link>
            <Link to="/contact" data-testid="hero-request-quote-btn"
              className="inline-flex items-center gap-2 bg-white/95 text-slate-900 font-semibold px-8 py-4 rounded-md border border-white/40 hover:bg-white transition-colors duration-200">
              Request a Quote
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Stats overlap */}
      <section className="relative -mt-16 z-20 max-w-6xl mx-auto px-6 w-full" ref={statsRef}>
        <div className="bg-white/90 backdrop-blur-xl border border-white/40 rounded-xl shadow-[0_20px_40px_-10px_rgba(15,23,42,0.15)] grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-100">
          {stats.map((s, i) => (
            <div key={i} className="p-6 lg:p-8" data-testid={`stat-card-${i}`}>
              <div className="font-['Outfit'] text-2xl lg:text-4xl font-black text-slate-900">
                {s.text ? s.text : <CountUp target={s.target} suffix={s.suffix} decimals={s.decimals || 0} started={statsSeen} />}
              </div>
              <div className="mt-1 text-xs lg:text-sm font-semibold text-slate-500">{s.label}</div>
              <div className="mt-3 h-1 w-10 bg-orange-500 rounded-full" />
            </div>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-6 lg:px-16 max-w-7xl mx-auto w-full">
        <motion.div {...fadeUp}>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">What We Do</div>
          <h2 className="font-['Outfit'] mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 max-w-lg">
            Logistics services built for Indian highways
          </h2>
        </motion.div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map(({ icon: Icon, title, desc }, i) => (
            <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
              className="bg-white border border-slate-100 rounded-xl p-8 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(15,23,42,0.12)] hover:-translate-y-2 transition-[transform,box-shadow] duration-300"
              data-testid={`service-card-${i}`}>
              <div className="w-12 h-12 rounded-md bg-orange-50 flex items-center justify-center text-orange-500">
                <Icon size={24} />
              </div>
              <h3 className="font-['Outfit'] mt-5 text-xl font-semibold text-slate-900">{title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust & Safety */}
      <section className="bg-slate-900 py-24 px-6 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeUp}>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">Trust &amp; Safety</div>
            <h2 className="font-['Outfit'] mt-3 text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-lg">
              Your goods travel safer with us
            </h2>
          </motion.div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {trust.map(({ icon: Icon, title, desc }, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                className="border border-slate-800 rounded-xl p-8 bg-slate-800/40" data-testid={`trust-card-${i}`}>
                <Icon size={28} className="text-orange-400" />
                <h3 className="font-['Outfit'] mt-4 text-lg font-semibold text-white">{title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-slate-800 pt-10">
            <div className="font-['Outfit'] text-xl lg:text-2xl font-bold text-white">Ready to move your next consignment?</div>
            <Link to="/contact" data-testid="cta-bottom-quote-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-semibold px-8 py-4 rounded-md shadow-lg shadow-orange-500/30 hover:bg-orange-600 transition-colors duration-200">
              Request a Quote <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
